/* ============================================================== */
/*   RASTERIZE เส้นที่วาดบนภาพ gallery -> data URI PNG (browser only) */
/* ============================================================== */
/*
 * เหตุผลที่มีไฟล์นี้แยกจาก examModelV4 (ซึ่งต้อง pure เพื่อ import ใน Node/Jest ได้)
 * ไฟล์นี้ใช้ document/canvas/Image จึงต้องไม่หลุดเข้า dependency graph ของ model test
 *
 * จำเป็นต้อง rasterize เพราะ backend ยอมรับเฉพาะรูปจริง (png/jpeg) เป็น data URI base64
 * verify จาก staging API แล้ว:
 *   POST /apis/DPO/medical_record/gallery/{emr}/  body image = data:image/svg+xml;base64,...
 *     -> 400 {"image":["ไม่รองรับรูปภาพที่อัพโหลด ไฟล์ที่อัพโหลดไม่ใช่รูป หรือ รูปภาพที่ผิดปกติ"]}
 *   PATCH /apis/DPO/medical_record/picture/{pk}/  body image = data:image/svg+xml;charset=utf-8,%3Csvg...
 *     -> 500 "Invalid base64-encoded string ..." (backend base64 decode payload ก่อนอื่น)
 *   image = data:image/png;base64,... -> 201/200
 *
 * หมายเหตุ: examModelV4 เคยมี flattenedImageSvg() (SVG data URI)
 * แต่ API ไม่รับ svg จึงลบออกจาก model แล้ว
 *
 * หมายเหตุ: URL ที่ API ตอบกลับใน field `image` เป็น signed URL ของ GCS
 * ซึ่งส่ง Access-Control-Allow-Origin กลับมาแล้ว (ตรวจด้วย curl -H 'Origin: http://localhost:3000')
 * จึงวางลง canvas พร้อม crossOrigin="anonymous" ได้ ไม่ทำให้ canvas ถูก taint
 */
import type { Stroke } from "./examModelV4";

export type RasterizeError = Error & { code?: string };

const loadBaseImage = (src: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`อ่านภาพเดิมไม่สำเร็จ: ${src}`));
    img.src = src;
  });

/** วาดภาพปลายทางแบบ cover (เสมอด้านสั้นออกขอบ) เหมือน preserveAspectRatio="xMidYMid slice" */
const drawCover = (
  context: CanvasRenderingContext2D,
  image: HTMLImageElement,
  width: number,
  height: number,
): void => {
  const scale = Math.max(width / image.width, height / image.height);
  const drawnWidth = image.width * scale;
  const drawnHeight = image.height * scale;
  context.drawImage(
    image,
    (width - drawnWidth) / 2,
    (height - drawnHeight) / 2,
    drawnWidth,
    drawnHeight,
  );
};

/**
 * รวมภาพเดิม + เส้นที่วาด ออกเป็น data:image/png;base64
 * ถ้าโหลดภาพเดิมไม่ได้ ต้อง reject (ห้ามส่งภาพที่มีแค่เส้นขึ้นไปทับภาพจริงในเวชระเบียน)
 *
 * หมายเหตุ (86630): หน้า Image Gallery ของ HIS V4 **ไม่ใช้ฟังก์ชันนี้แล้ว**
 * เพราะ backend เพิ่ม DPO.ClinicalPictureAnnotation (Redmine #86780) เก็บเส้นเป็น layer
 * แยกจากไฟล์ภาพต้นฉบับ การ bake เส้นทับภาพจึงขัดกับโจทย์ข้อ 3
 * คงไว้สำหรับงานอื่นที่ยังต้องรวมภาพเป็นไฟล์เดียว (และใช้เป็นหลักฐานว่าเคยทดสอบ contract ของ API)
 */
export async function rasterizeStrokesToPng(args: {
  image?: string;
  strokes: Stroke[];
  width: number;
  height: number;
}): Promise<string> {
  const base = args.image ? await loadBaseImage(args.image) : null;

  const canvas = document.createElement("canvas");
  canvas.width = args.width;
  canvas.height = args.height;
  const context = canvas.getContext("2d");
  if (!context) {
    throw new Error("เบราว์เซอร์ไม่รองรับ canvas 2D");
  }

  context.fillStyle = "#ffffff";
  context.fillRect(0, 0, args.width, args.height);
  if (base) drawCover(context, base, args.width, args.height);

  for (const stroke of args.strokes) {
    context.strokeStyle = stroke.color;
    context.lineWidth = stroke.width;
    context.lineCap = "round";
    context.lineJoin = "round";
    const points = stroke.points;

    if (points.length === 1) {
      const [x, y] = points[0];
      context.beginPath();
      context.arc(x, y, stroke.width / 2, 0, Math.PI * 2);
      context.fillStyle = stroke.color;
      context.fill();
      continue;
    }

    context.beginPath();
    points.forEach(([x, y], index) =>
      index === 0 ? context.moveTo(x, y) : context.lineTo(x, y),
    );
    context.stroke();
  }

  try {
    return canvas.toDataURL("image/png");
  } catch (error) {
    /* canvas ถูก taint เมื่อภาพเดิมไม่ส่ง CORS กลับมา */
    const message: RasterizeError = new Error(
      "รวมเส้นกับภาพเดิมไม่สำเร็จ (ภาพเดิมไม่เปิดให้ใช้กับ canvas)",
    );
    message.code = "TAINTED";
    throw message;
  }
}

/**
 * URL ของ ภาพ (รูปที่ API ตอบกลับ / ไฟล์ template ของแผนก) -> data:image/...;base64
 *
 * จำเป็นต้องแปลงก่อน POST /apis/DPO/medical_record/gallery/{emr}/
 * เพราะ backend ไม่รับ URL (verify จาก staging แล้ว: 400 {"image":["ไม่รองรับรูปภาพที่อัพโหลด …"]})
 * และ V3 ก็ทำเหมือนกัน — ModalTemplateFile แสดง url แล้วส่งค่าที่โหลดมาแล้วเป็น base64 (`CardImageGallery.tsx:370-399`)
 */
export type DataUriResult = { ok: true; image: string } | { ok: false; error: string };

const RASTER_TYPES = ["image/png", "image/jpeg", "image/jpg", "image/webp", "image/gif"];

export const dataUriFromImageUrl = async (url: string): Promise<DataUriResult> => {
  if (url.trim() === "") return { ok: false, error: "ไม่มี URL ของภาพ" };
  /* data URI อยู่แล้ว (เช่นรูปที่ canvas สร้างขึ้นในหน้านี้) -> ใช้ได้เลย */
  if (url.startsWith("data:image/")) {
    return url.startsWith("data:image/svg")
      ? { ok: false, error: "backend ไม่รับ svg เป็นภาพ (ต้องใช้ png/jpeg เท่านั้น)" }
      : { ok: true, image: url };
  }

  if (typeof fetch !== "function") return { ok: false, error: "browser ไม่ให้โหลดภาพด้วย fetch" };

  let response: Response;
  try {
    response = await fetch(url, { mode: "cors" });
  } catch (error) {
    return { ok: false, error: "โหลดภาพไม่สำเร็จ (เชื่อมต่อไม่ได้)" };
  }

  if (!response.ok) return { ok: false, error: `โหลดภาพไม่สำเร็จ (${response.status})` };

  const type = `${response.headers.get("content-type") ?? ""}`.split(";")[0].trim();
  if (type !== "" && RASTER_TYPES.indexOf(type) === -1) {
    return { ok: false, error: `backend ไม่รับภาพประเภท ${type} (ต้องใช้ png/jpeg เท่านั้น)` };
  }

  const blob = await response.blob();
  /* blob -> base64 (FileReader เป็น browser API) */
  const dataUri = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(`${reader.result ?? ""}`);
    reader.onerror = () => reject(new Error("read blob ไม่สำเร็จ"));
    reader.readAsDataURL(blob);
  }).catch(() => "");

  if (!dataUri.startsWith("data:image/") || dataUri.startsWith("data:image/svg")) {
    return { ok: false, error: "ภาพที่โหลดมาไม่ใช่รูปแบบที่ backend รับ (png/jpeg)" };
  }

  return { ok: true, image: dataUri };
};
